<!-- MOLECULE: CorporateResultsActions.vue -->
<!-- App bar actions for the corporate visit results page: refresh, email, PDF download. -->

<script setup lang="ts">
//
// Imports
//

import AppBarActionButton from "./AppBarActionButton.vue";

//
// Composables
//

const { t } = useI18n();
const { showSnackbar } = useSnackbar();

/**
 * Loading state for the corporate results page.
 * Reads the same useState key that useCorporateResultsPage writes to via
 * useVisits("corporate-results"), so the refresh button reflects the real
 * loading state without instantiating the page composable.
 */
const { loading, currentVisitResults } = useVisits("corporate-results");

/** Email/PDF actions are disabled when results are unavailable or empty. */
const areActionsDisabled = computed(
  () => !currentVisitResults.value || currentVisitResults.value.departmentResults.length === 0,
);

/**
 * Shared refresh trigger — useCorporateResultsPage watches this useState
 * counter. Incrementing it triggers a reload without instantiating the
 * full page composable (which would register duplicate watchers + onMounted).
 */
const refreshTrigger = useState<number>("corporate-results.refreshTrigger", () => 0);

//
// Methods
//

function triggerRefresh(): void {
  refreshTrigger.value++;
}

/** Show a "not implemented" snackbar for actions that are not yet available. */
function handleNotImplemented(): void {
  showSnackbar(t("components.common.snackbar.actions.featureNotImplemented"), {
    color: "error",
    icon: "mdi-hammer-wrench",
  });
}
</script>

<template>
  <app-bar-action-button
    icon="mdi-refresh"
    :tooltip-text="t('pages.otp-patient.results.refresh.tooltip')"
    :loading="loading"
    :disabled="loading"
    @click="triggerRefresh"
  />
  <app-bar-action-button
    icon="mdi-email-outline"
    :tooltip-text="t('pages.otp-patient.results.email.tooltip')"
    :disabled="areActionsDisabled"
    @click="handleNotImplemented"
  />
  <app-bar-action-button
    icon="mdi-file-pdf-box"
    :tooltip-text="t('pages.otp-patient.results.pdf.tooltip')"
    :disabled="areActionsDisabled"
    @click="handleNotImplemented"
  />
</template>
